import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faStar,
  faQuoteLeft,
  faChevronLeft,
  faChevronRight,
  faTruck,
  faBuilding
} from '@fortawesome/free-solid-svg-icons';

gsap.registerPlugin(ScrollTrigger);

const TestimonialsSection = () => {
  const sectionRef = useRef(null);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const testimonialRef = useRef(null);

  const testimonials = [
    {
      id: 1,
      name: '张总',
      company: '顺丰速运',
      position: '财务总监',
      companyInitial: 'SF',
      companyColor: 'bg-blue-600',
      rating: 5,
      content: '系统的实时数据同步功能让我们能够及时掌握财务状况，做出更准确的业务决策。客服团队的专业支持也让我们倍感安心。',
      metrics: {
        efficiency: '300%',
        accuracy: '99.8%',
        timeSaved: '80%'
      },
      industry: '零担物流'
    },
    {
      id: 2,
      name: '李经理',
      company: '安能物流',
      position: '运营经理',
      companyInitial: 'AN',
      companyColor: 'bg-green-600',
      rating: 5,
      content: '云帐天下的智能财务系统完全改变了我们的财务管理方式。自动化的凭证生成功能让我们的工作效率提升了300%，数据准确性也大大提高。',
      metrics: {
        efficiency: '250%',
        accuracy: '99.5%',
        timeSaved: '70%'
      },
      industry: '零担物流'
    },
    {
      id: 3,
      name: '王总',
      company: '沪宁专线',
      position: 'CFO',
      companyInitial: 'HN',
      companyColor: 'bg-purple-600',
      rating: 5,
      content: '作为一家大型物流企业，我们对系统的稳定性和安全性要求极高。云帐天下不仅满足了我们的需求，还超出了我们的期望。',
      metrics: {
        efficiency: '400%',
        accuracy: '99.9%',
        timeSaved: '85%'
      },
      industry: '专线网络'
    }
  ];

  useEffect(() => {
    const section = sectionRef.current;
    
    // 入场动画
    gsap.fromTo(
      section.children,
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.2,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: section,
          start: 'top 70%',
          toggleActions: 'play none none reverse'
        }
      }
    );

    // 自动轮播
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 6000);

    return () => {
      clearInterval(interval);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  useEffect(() => {
    // 切换动画
    if (testimonialRef.current) {
      gsap.fromTo(
        testimonialRef.current,
        { x: 50, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.5, ease: 'power2.out' }
      );
    }
  }, [currentTestimonial]);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <FontAwesomeIcon
        key={index}
        icon={faStar}
        className={`${index < rating ? 'text-yellow-400' : 'text-gray-300'} text-sm`}
      />
    ));
  };

  const currentData = testimonials[currentTestimonial];

  return (
    <section ref={sectionRef} className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="container mx-auto px-4">
        {/* 标题 */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold gradient-text mb-6">
            客户成功案例
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            听听我们的客户如何通过云帐天下实现财务管理的数字化转型
          </p>
        </div>

        {/* 主要内容区域 */}
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* 左侧：客户信息和评价 */}
              <div className="p-8 lg:p-12">
                <div ref={testimonialRef}>
                  {/* 引用图标 */}
                  <FontAwesomeIcon 
                    icon={faQuoteLeft} 
                    className="text-4xl text-primary-blue mb-6 opacity-20" 
                  />
                  
                  {/* 评价内容 */}
                  <blockquote className="text-lg lg:text-xl text-gray-700 leading-relaxed mb-8">
                    "{currentData.content}"
                  </blockquote>
                  
                  {/* 星级评分 */}
                  <div className="flex items-center space-x-1 mb-6">
                    {renderStars(currentData.rating)}
                    <span className="ml-2 text-sm text-gray-600">({currentData.rating}.0)</span>
                  </div>
                  
                  {/* 客户信息 */}
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <div className={`w-16 h-16 rounded-full ${currentData.companyColor} border-4 border-primary-blue/20 flex items-center justify-center`}>
                        <span className="text-white font-bold text-lg">{currentData.companyInitial}</span>
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-primary-blue rounded-full flex items-center justify-center">
                        <FontAwesomeIcon icon={faTruck} className="text-white text-xs" />
                      </div>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">{currentData.name}</h4>
                      <p className="text-primary-blue font-medium">{currentData.position}</p>
                      <p className="text-gray-600 text-sm">{currentData.company}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* 右侧：数据指标 */}
              <div className="bg-gradient-primary p-8 lg:p-12 text-white">
                <h3 className="text-2xl font-bold mb-8">实施效果</h3>
                
                <div className="space-y-6">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-blue-100">效率提升</span>
                      <FontAwesomeIcon icon={faChevronRight} className="text-primary-cyan" />
                    </div>
                    <div className="text-3xl font-bold text-primary-cyan">
                      +{currentData.metrics.efficiency}
                    </div>
                  </div>
                  
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-blue-100">数据准确率</span>
                      <FontAwesomeIcon icon={faChevronRight} className="text-primary-cyan" />
                    </div>
                    <div className="text-3xl font-bold text-primary-cyan">
                      {currentData.metrics.accuracy}
                    </div>
                  </div>
                  
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-blue-100">时间节省</span>
                      <FontAwesomeIcon icon={faChevronRight} className="text-primary-cyan" />
                    </div>
                    <div className="text-3xl font-bold text-primary-cyan">
                      {currentData.metrics.timeSaved}
                    </div>
                  </div>
                </div>
                
                <div className="mt-8 p-4 bg-white/10 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <FontAwesomeIcon icon={faBuilding} className="text-primary-cyan" />
                    <span className="text-sm text-blue-100">行业：{currentData.industry}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 导航控制 */}
        <div className="flex items-center justify-center mt-12 space-x-4">
          <button
            onClick={prevTestimonial}
            className="w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-primary-blue hover:text-white transition-all duration-300"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          
          <div className="flex space-x-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentTestimonial(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentTestimonial
                    ? 'bg-primary-blue scale-125'
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>
          
          <button
            onClick={nextTestimonial}
            className="w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-primary-blue hover:text-white transition-all duration-300"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;